import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

const Login = () => {
  const auth = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [signInError, setSignInError] = useState('');

  useEffect(() => {
    sessionStorage.removeItem('justLoggedOut');
  }, []);

  const handleSignIn = async () => {
    setIsRedirecting(true);
    setSignInError('');

    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Failed to redirect to Cognito sign in', error);
      setSignInError('AMX could not open the secure sign-in page. Please try again.');
      setIsRedirecting(false);
    }
  };

  if (auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#eef3f8',
        color: '#17324d',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: { xs: 3, sm: 6 },
      }}
    >
      <Paper
        component="main"
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 760,
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid #c5d3e0',
        }}
      >
        <Box
          sx={{
            bgcolor: '#12395b',
            color: '#fff',
            px: { xs: 3, sm: 5 },
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: '0.12em', opacity: 0.85 }}
          >
            Agile Manufacturing Exchange
          </Typography>
          <Typography component="h1" variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>
            Secure Sign In
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Box
            role="note"
            aria-labelledby="system-use-notice-title"
            sx={{
              border: '2px solid #9c2f2f',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#fff8e6',
            }}
          >
            <Box sx={{ bgcolor: '#9c2f2f', color: '#fff', px: 3, py: 1.5 }}>
              <Typography
                id="system-use-notice-title"
                component="h2"
                variant="h6"
                sx={{ fontWeight: 800, letterSpacing: '0.04em' }}
              >
                CMMC / CUI System Use Notice
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
              <Typography sx={{ fontWeight: 800, color: '#721c24', mb: 1.5 }}>
                AUTHORIZED USE ONLY
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.65, mb: 1.5 }}>
                This system is restricted to authorized users and may contain Controlled Unclassified
                Information (CUI). Use of this system must comply with applicable laws, regulations,
                contracts, and AMX security policies.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.65, mb: 1.5 }}>
                Activity on this system may be monitored, recorded, and audited. Unauthorized access,
                misuse, or disclosure of information may result in loss of access, disciplinary action,
                and civil or criminal penalties.
              </Typography>
              <Box
                sx={{
                  bgcolor: '#ffe2a8',
                  borderLeft: '5px solid #b35c00',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography sx={{ fontWeight: 700, lineHeight: 1.55 }}>
                  By continuing and logging in, you acknowledge this notice, consent to authorized
                  monitoring, and agree to comply with these terms.
                </Typography>
              </Box>
            </Box>
          </Box>

          {signInError ? (
            <Alert severity="error" sx={{ mt: 3 }}>
              {signInError}
            </Alert>
          ) : null}

          <Button
            type="button"
            variant="contained"
            size="large"
            fullWidth
            disabled={auth.isLoading || isRedirecting}
            onClick={handleSignIn}
            sx={{ mt: 3, minHeight: 52, fontWeight: 700 }}
          >
            {auth.isLoading || isRedirecting ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1.5 }} />
                Opening Secure Sign-In…
              </>
            ) : (
              'Acknowledge and Continue to Sign In'
            )}
          </Button>

          <Button component={Link} to="/welcome" color="inherit" fullWidth sx={{ mt: 1.5 }}>
            Return to AMX Home
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
